import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import './HomePage.css';

const WHO_FOR_BULLETS = [
  "You're thinking about buying your first rental property in England — or your second — and you want to understand what you're getting into before you commit.",
  'You want plain, honest training — not a sales funnel that pushes you towards a £5,000 seminar.',
  'You want to understand the rules, the numbers and the risks of being a landlord — properly — before you part with any money.',
];

const TESTIMONIALS = [
  {
    id: 1,
    quote: "I wish I'd had this before I bought my first rental property. It would have saved me three expensive mistakes in the first six months.",
  },
  {
    id: 2,
    quote: "Finally, property training that doesn't feel like a sales pitch. Amanda explains the rules and the risks in plain English.",
  },
  {
    id: 3,
    quote: "The Ask Amanda feature is genuinely useful. I asked a question at 11pm on a Tuesday and got a clear, accurate answer.",
  },
];

const FAQS = [
  {
    id: 1,
    question: 'Is this course England-specific?',
    answer: 'Yes. The course is built specifically for buying and letting residential property in England. The legal rules, the licensing, the tax position and the documents are all based on English law and current HMRC guidance. Scotland, Wales and Northern Ireland have different rules in several important areas — if you\'re buying there, this course won\'t cover everything you need.',
  },
  {
    id: 2,
    question: 'Do I need any prior property knowledge?',
    answer: 'None at all. The course is written for someone who is thinking about buying their first rental property and wants to understand what they\'re getting into before they commit any money. We start from the beginning and build up — no jargon, no assumed knowledge.',
  },
  {
    id: 3,
    question: 'Is this just a sales funnel for something more expensive?',
    answer: 'No. This is the course. There is no follow-up seminar, no mentorship programme, no mastermind group, and no upsell of any kind. You pay once and you get everything — all the lessons, all the materials, Amanda AI, and lifetime access. That\'s it.',
  },
  {
    id: 4,
    question: 'How long do I have access for?',
    answer: 'To make the most of the content, access is granted for 90 days. You can log-in and out as many times as you need but please do not share your login details with another user. We can track this.',
  },
  {
    id: 5,
    question: 'What is Amanda AI and how does it work?',
    answer: 'Amanda AI is a built-in AI tutor that sits inside every lesson. She knows which module and lesson you\'re currently on, so when you ask a question you get a contextual answer — not a generic response. You can ask about anything covered in the course, ask her to explain something differently, or ask follow-up questions. She\'s available 24 hours a day.',
  },
  {
    id: 6,
    question: 'Can I get a refund if it\'s not right for me?',
    answer: 'Yes. If you complete fewer than three lessons and decide the course isn\'t right for you, contact us within 14 days of purchase and we\'ll refund you in full — no questions asked. We\'re confident the course delivers real value, but we don\'t want anyone to feel stuck with something that doesn\'t work for them.',
  },
];

/* =============================================================================
   SHARED SVG COMPONENTS
   ============================================================================= */

function TickIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path d="M2 7.5L5.5 11L12 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PlusIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path d="M7 2V12M2 7H12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function PlayIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M8 5.14v14l11-7-11-7z" />
    </svg>
  );
}

function PauseIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M6 5h4v14H6V5zm8 0h4v14h-4V5z" />
    </svg>
  );
}

/* =============================================================================
   SUB-COMPONENTS
   ============================================================================= */

function CourseCard({ course }) {
  const isLive = course.status === 'live';
  return (
    <div
      className={`course-card ${isLive ? 'course-card--live' : 'course-card--soon'}`}
      onClick={() => isLive && course.slug && (window.location.href = `/courses/${course.slug}`)}
    >
      <div className="course-card__thumb">
        <span className="course-card__icon">{course.icon}</span>
        {isLive && <span className="course-card__available-tag">Available now</span>}
      </div>
      <div className="course-card__body">
        <span className={`course-card__badge ${isLive ? 'course-card__badge--live' : 'course-card__badge--soon'}`}>
          {isLive ? 'Live now' : 'Coming soon'}
        </span>
        <h3 className="course-card__title">{course.title}</h3>
        <p className="course-card__tagline">{course.tagline}</p>
        <div className="course-card__meta">
          <span className="course-card__price">{course.price}</span>
          <span className="course-card__stats">{course.modules} modules · {course.lessons} lessons</span>
        </div>
      </div>
    </div>
  );
}

function TestimonialCard({ testimonial }) {
  return (
    <div className="testimonial-card">
      <p className="testimonial-card__quote">&ldquo;{testimonial.quote}&rdquo;</p>
      <div className="testimonial-card__attribution">
        <div>
          <p className="testimonial-card__name">{testimonial.name}</p>
          <p className="testimonial-card__location">{testimonial.location}</p>
        </div>
      </div>
    </div>
  );
}

function FaqItem({ faq }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="faq-item">
      <button
        className="faq-item__trigger"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span className="faq-item__question">{faq.question}</span>
        <span className={`faq-item__icon ${open ? 'faq-item__icon--open' : ''}`}>
          <PlusIcon className="w-3.5 h-3.5 text-bright-teal" />
        </span>
      </button>
      {open && (
        <div className="faq-item__answer">
          {faq.answer}
        </div>
      )}
    </div>
  );
}

const VIMEO_VIDEO_ID = '1206046555';

function DemoVideo({ priceLabel }) {
  const sectionRef = useRef(null);
  const iframeRef = useRef(null);
  const playerRef = useRef(null);

  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [sdkReady, setSdkReady] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (window.Vimeo) { setSdkReady(true); return; }
    const script = document.createElement('script');
    script.src = 'https://player.vimeo.com/api/player.js';
    script.onload = () => setSdkReady(true);
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    if (!sdkReady || !iframeRef.current || playerRef.current) return;

    const player = new window.Vimeo.Player(iframeRef.current);
    playerRef.current = player;

    player.on('play', () => setPlaying(true));
    player.on('pause', () => setPlaying(false));
    player.on('ended', () => { setPlaying(false); setMuted(true); });

    player.setMuted(true)
      .then(() => player.play())
      .catch(() => { });
  }, [sdkReady]);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  function handleOverlayClick() {
    const player = playerRef.current;
    if (!player) return;

    if (playing && muted) {
      player.setMuted(false)
        .then(() => player.setVolume(1))
        .then(() => setMuted(false))
        .catch(() => { });
    } else if (playing) {
      player.pause();
    } else {
      player.setMuted(false)
        .then(() => player.setVolume(1))
        .then(() => player.play())
        .then(() => setMuted(false))
        .catch(() => { });
    }
  }

  return (
    <section id="demo-video" className={`hp-demo ${visible ? 'hp-demo--visible' : ''}`} ref={sectionRef} style={{ opacity: 1 }}>
      <div className="hp-demo__inner">
        <h2 className="hp-demo__heading" style={{ marginBottom: '1.75rem' }}>
          Meet Amanda
        </h2>

        <div className="hp-demo__frame" style={{ marginBottom: '2rem' }}>
          <iframe
            ref={iframeRef}
            className="hp-demo__iframe"
            src={`https://player.vimeo.com/video/${VIMEO_VIDEO_ID}?autopause=0&muted=1&autoplay=1&title=0&byline=0&portrait=0&controls=0&dnt=1`}
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            title="Module 1, Lesson 1 — preview"
          />

          <button
            className={`hp-demo__overlay ${playing && !muted ? 'hp-demo__overlay--playing' : ''}`}
            onClick={handleOverlayClick}
            aria-label={
              playing && muted ? 'Click to unmute' :
                playing ? 'Pause preview' :
                  'Play preview'
            }
          >
            <span className="hp-demo__play-ring">
              {playing && !muted
                ? <PauseIcon className="hp-demo__play-icon hp-demo__play-icon--pause" />
                : <PlayIcon className="hp-demo__play-icon hp-demo__play-icon--play" />
              }
            </span>
          </button>

          {playing && muted && (
            <span
              className="hp-demo__badge"
              style={{
                left: 'auto',
                right: '0.75rem',
                top: '0.75rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.3rem',
              }}
            >
              🔇 Click for sound
            </span>
          )}
        </div>

        <div className="hp-demo__cta">
          <Link to="/courses/your-first-rental-property" className="btn-cta-primary">
            Start the course — {priceLabel} →
          </Link>
        </div>
      </div>
    </section>
  );
}

/* =============================================================================
   PAGE
   ============================================================================= */

export default function HomePage() {
 const [pricePence, setPricePence] = useState(null); // null = not loaded yet

useEffect(() => {
  api.get('/courses/your-first-rental-property')
    .then(r => setPricePence(r.data?.price_pence ?? 4700))
    .catch(() => setPricePence(4700)); 
}, []);

const priceLabel = pricePence == null ? '…' : `£${(pricePence / 100).toFixed(0)}`;

  return (
    <div className="hp-page">

      <section className="hp-hero">
        <div className="hp-hero__overlay" aria-hidden="true" />
        <div className="hp-hero__wave" aria-hidden="true" />

        <div className="hp-hero__inner">
          <div className="hp-hero__badge">AI-Powered UK Property Training</div>

          <h1 className="hp-hero__h1">
            Learn buy-to-let.<br />Without the £5,000 seminar.
          </h1>

          <p className="hp-hero__sub">
            Honest, plain-English property training, delivered by Amanda AI — your AI tutor,
            built on 45 years of real-world experience as landlords, investors and managing agents.
          </p>

          <div className="hp-hero__ctas">
            <Link to="/courses/your-first-rental-property" className="btn-cta-primary">
              Start the course — {priceLabel} →
            </Link>

            <a
              href="#demo-video"
              className="btn-cta-secondary"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('demo-video')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
            Watch Free Demo
          </a>
        </div>

        <p className="hp-hero__trust">30 lessons. One price. No upselling. 14-day refund.</p>
    </div>
      </section >

      <DemoVideo priceLabel={priceLabel} />

      <section className="hp-who">
        <div className="hp-inner--narrow text-center">
          <h2 className="hp-heading">This course is for you if…</h2>

          <ul className="hp-who__list mt-8">
            {WHO_FOR_BULLETS.map((bullet, i) => (
              <li key={i} className="hp-who__item">
                <span className="hp-who__tick">
                  <TickIcon className="w-3.5 h-3.5 text-primary-navy" />
                </span>
                <p className="hp-who__text">{bullet}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="hp-upsell">
        <div className="hp-inner--narrow text-center">
          <div className="hp-upsell__icon-wrap">
            <div className="hp-upsell__icon-ring">
              <TickIcon className="w-[22px] h-[22px] text-bright-teal" />
            </div>
          </div>

          <p className="hp-upsell__line">
            <span className="hp-upsell__phrase">One course</span>
            <span className="hp-upsell__connector">. </span>
            <span className="hp-upsell__phrase">One price</span>
            <span className="hp-upsell__connector">. </span>
            <span className="hp-upsell__phrase">No follow-up seminars</span>
            <span className="hp-upsell__connector">. </span>
            <span className="hp-upsell__phrase">No upselling</span>
            <span className="hp-upsell__connector">.</span>
          </p>

          <p className="hp-upsell__closing">Everything you need is here.</p>
        </div>
      </section>

      <section className="hp-testimonials">
        <div className="hp-inner">
          <p className="hp-label">Social proof</p>
          <h2 className="hp-heading">What people who've seen it are saying</h2>
          <p className="hp-desc">Real feedback from beta testers.</p>

          <div className="hp-testimonials__grid">
            {TESTIMONIALS.map((t) => <TestimonialCard key={t.id} testimonial={t} />)}
          </div>
        </div>
      </section>

      <section className="hp-faq">
        <div className="hp-inner">
          <p className="hp-label">Common questions</p>
          <h2 className="hp-heading">Everything you want to know</h2>

          <div className="hp-faq__list">
            {FAQS.map((f) => <FaqItem key={f.id} faq={f} />)}
          </div>
        </div>
      </section>

      <section className="hp-cta">
        <div className="hp-cta__inner">
          <h2 className="hp-cta__heading">Ready to buy your first rental property with confidence?</h2>
          <p className="hp-cta__sub">Join the students already learning with Amanda AI.</p>
          <Link to="/courses/your-first-rental-property" className="btn-cta-primary--dark">
            Start the course — {priceLabel} →
          </Link>
        </div>
      </section>

    </div >
  );
}